import Extension from "../base/extension";

/**
 * Background video behaviour for [data-background-video] wrappers:
 *
 * - Respects prefers-reduced-motion: strips autoplay and pauses every hosted
 *   video, leaving the poster frame visible. Applies on load and when the
 *   media query changes.
 * - Powers the visible pause/play toggle (WCAG 2.2.2): a delegated click
 *   handler on [data-background-video-toggle] pauses/plays all hosted videos
 *   inside the nearest [data-background-video-group] (both the desktop and
 *   mobile breakpoint copies), updating the button's aria-pressed state,
 *   aria-label and icon. The toggle still allows explicit play when the
 *   reduced-motion guard stripped autoplay.
 *
 * Cross-origin embeds (YouTube/Vimeo iframes) cannot be paused from here and
 * render no toggle; that limitation is documented in
 * docs/data-attribute-contracts.md.
 */
class BackgroundVideo extends Extension {
  name = "BackgroundVideo";

  constructor(name) {
    super(name);
  }

  init() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reducedMotion.addEventListener("change", () => this.applyGuard());

    // Delegated: toggles are re-rendered with their sections (theme editor).
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-background-video-toggle]");
      if (toggle) this.onToggleClick(toggle);
    });

    // Non-looping videos (autoplay disabled in settings) end on their own;
    // flip the toggle back to its "play" state so it stays honest. `ended`
    // does not bubble, so listen in the capture phase.
    document.addEventListener(
      "ended",
      (event) => {
        const group = event.target?.closest?.("[data-background-video-group]");
        if (!group) return;
        const toggle = group.querySelector("[data-background-video-toggle]");
        if (toggle) this.setToggleState(toggle, true);
      },
      true
    );
  }

  onReady() {
    this.applyGuard();
  }

  applyGuard() {
    if (!this.reducedMotion.matches) return;
    document.querySelectorAll("[data-background-video] video").forEach((video) => {
      video.removeAttribute("autoplay");
      video.pause();
    });

    // Keep any pause/play toggles in sync with the now-paused videos.
    document.querySelectorAll("[data-background-video-toggle]").forEach((toggle) => {
      this.setToggleState(toggle, true);
    });
  }

  /**
   * Pause or play every hosted video in the toggle's wrapper group.
   * aria-pressed="true" means the paused state is engaged.
   *
   * @param {HTMLElement} toggle The clicked [data-background-video-toggle]
   */
  onToggleClick(toggle) {
    const group = toggle.closest("[data-background-video-group]");
    if (!group) return;
    const videos = group.querySelectorAll("[data-background-video] video");
    if (!videos.length) return;

    const paused = toggle.getAttribute("aria-pressed") === "true";
    if (paused) {
      // Explicit user request to play wins over the reduced-motion guard.
      videos.forEach((video) => video.play()?.catch(() => {}));
      this.setToggleState(toggle, false);
    } else {
      videos.forEach((video) => video.pause());
      this.setToggleState(toggle, true);
    }
  }

  /**
   * Reflect the paused/playing state on the toggle button: aria-pressed,
   * translated aria-label (from the button's data attributes) and glyph.
   *
   * @param {HTMLElement} toggle
   * @param {Boolean} paused
   */
  setToggleState(toggle, paused) {
    toggle.setAttribute("aria-pressed", paused ? "true" : "false");
    const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
    toggle.querySelector('[data-toggle-icon="pause"]')?.classList.toggle("d-none", paused);
    toggle.querySelector('[data-toggle-icon="play"]')?.classList.toggle("d-none", !paused);
  }
}

export default BackgroundVideo;
